/**
 * **The blog** — FR-I7, T-4.6; redrawn in Wave 14D (C13, spec §10.5 item 6;
 * T-4.64).
 *
 * A list with a search box and a status filter; each post opens on its own
 * page, `/site/blog/:postId` (`BlogPost.tsx`), on the edit bar. The list itself
 * edits nothing, so it has no bar.
 *
 * Posts are rich_text with a title, a slug, a date, an author and a published
 * flag. Nothing here is a second content system: a post's body goes through the
 * same typed-node editor and the same validator as a page's `rich_text` block,
 * because two content pipelines is two sanitisers and one of them is the one
 * that is wrong.
 *
 * **A post's status is independent of the site's draft.** A published post
 * appears on the site's blog page as soon as the site itself is published — it
 * does not need a second publish — because `_resolve_blog_block` reads the
 * posts live at render, the same way a `services` block reads the catalog.
 *
 * **Search and the filter run over the list the service returned** (its newest
 * hundred, `SiteRepo.posts`). A firm with more than a hundred posts is not one
 * this list has met; the day it is, the search moves to the service.
 */

import {
  Alert,
  Badge,
  Button,
  Card,
  CardBody,
  ConfirmDialog,
  Dialog,
  EmptyState,
  Input,
  Skeleton,
  buttonClass,
} from '@fiveox/ui'
import { ChevronRight, FileText, Pencil, PenLine, Plus, Search, Trash2 } from 'lucide-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router'

import { EditPage } from '../../components/edit'
import { SITE, useEvaluation } from '../../lib/access'
import { ApiError } from '../../lib/api'
import { site, type Post, type PostStatus, type PostTopic } from '../../lib/api/site'

export type BlogFilter = 'all' | 'published' | 'draft'

export const STATUS_LABEL: Record<PostStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Deleted',
}

/** One line under a post's title — its date and author, or what is missing. */
export function postMeta(post: Pick<Post, 'published_on' | 'author'>): string {
  const date = post.published_on ? formatPostDate(post.published_on) : 'No date'
  return post.author ? `${date} · ${post.author}` : date
}

/** `2026-09-01` → `1 Sep 2026`. Parsed by hand: `new Date('2026-09-01')` is
 *  midnight UTC, which is the previous evening west of Greenwich. */
export function formatPostDate(iso: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso)
  if (!m) return iso
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
  return `${Number(m[3])} ${months[Number(m[2]) - 1] ?? m[2]} ${m[1]}`
}

/** The list the firm sees for a search and a filter — pure, so it is tested
 *  without mounting. Matches the title, the summary and the author. */
export function filterPosts(posts: Post[], query: string, filter: BlogFilter): Post[] {
  const q = query.trim().toLowerCase()
  return posts.filter((post) => {
    if (post.status === 'archived') return false
    if (filter !== 'all' && post.status !== filter) return false
    if (!q) return true
    return [post.title, post.summary ?? '', post.author ?? ''].some((text) => text.toLowerCase().includes(q))
  })
}

/**
 * **One line** — C24 item 5 (*"need small and informative discription. single
 * line each"*). It used to add that a published post needs no second publish;
 * that caveat is said where it is needed, on the post itself beside Publish,
 * rather than on a screen a firm reads once.
 */
const BLOG_DESCRIPTION =
  'Notes, updates and explainers for your clients, published straight to your site’s blog.'

/**
 * **The firm's blog topics** — C24 item 15, ADR-0050 §1.
 *
 * The owner: *"blogs should have category or topic, so at website it can get
 * sorted by it"*. The firm's own list: add, rename, delete. A new site is
 * seeded with a starter list for its profession by the read below, so this is
 * never an empty box on a firm's first visit.
 *
 * **Deleting unlinks and keeps every post** (ADR-0050 §1), and the confirm
 * says so, because *delete* next to a list of posts reads like *delete the
 * posts*.
 *
 * **A rename moves the public address** `/blog?topic=<slug>`, which is the
 * firm's to decide as a page's address is — and the line under the field says
 * it rather than leaving the firm to find out from a broken link somebody
 * else shared.
 */
export function TopicManager({ editable }: { editable: boolean }) {
  const [topics, setTopics] = useState<PostTopic[] | null>(null)
  const [adding, setAdding] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [removing, setRemoving] = useState<PostTopic | null>(null)
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)

  const load = useCallback(() => {
    void site
      .topics()
      .then((body) => setTopics(body.data))
      .catch(() => setError('Your topics could not be loaded.'))
  }, [])

  useEffect(load, [load])

  const run = async (work: () => Promise<unknown>) => {
    setBusy(true)
    setError(null)
    try {
      await work()
      load()
      return true
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'That could not be saved.')
      return false
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card data-topic-manager="">
      <CardBody className="space-y-3">
        <div>
          <h2 className="text-foreground text-base font-semibold">Topics</h2>
          <p className="text-muted-foreground text-sm">
            Your visitors sort your blog by these. A post can have more than one.
          </p>
        </div>

        {error && <Alert tone="error">{error}</Alert>}

        {topics === null ? (
          <Skeleton className="h-10 w-full rounded-lg" />
        ) : (
          <ul className="flex flex-wrap gap-2" aria-label="Topics">
            {topics.map((topic) => (
              <li
                key={topic.id}
                data-topic={topic.slug}
                className="border-border bg-background inline-flex items-center gap-1 rounded-full border py-1 pr-1 pl-3 text-sm"
              >
                <span className="text-foreground">{topic.name}</span>
                {editable && (
                  <>
                    <button
                      type="button"
                      aria-label={`Rename ${topic.name}`}
                      className="text-muted-foreground hover:text-foreground rounded-full p-1"
                      onClick={() => setRenaming({ id: topic.id, name: topic.name })}
                    >
                      <Pencil size={12} aria-hidden />
                    </button>
                    <button
                      type="button"
                      aria-label={`Delete ${topic.name}`}
                      className="text-muted-foreground hover:text-destructive rounded-full p-1"
                      onClick={() => setRemoving(topic)}
                    >
                      <Trash2 size={12} aria-hidden />
                    </button>
                  </>
                )}
              </li>
            ))}
            {topics.length === 0 && (
              <li className="text-muted-foreground text-sm">No topics yet.</li>
            )}
          </ul>
        )}

        {editable && (
          <form
            className="flex flex-wrap items-end gap-2"
            onSubmit={(e) => {
              e.preventDefault()
              const name = adding.trim()
              if (!name) return
              void run(() => site.createTopic(name)).then((ok) => ok && setAdding(''))
            }}
          >
            <div className="min-w-0 flex-1">
              <Input
                aria-label="A new topic"
                placeholder="Income tax"
                value={adding}
                maxLength={80}
                onChange={(e) => setAdding(e.target.value)}
              />
            </div>
            <Button type="submit" variant="outline" disabled={busy || !adding.trim()}>
              <Plus size={14} aria-hidden /> Add a topic
            </Button>
          </form>
        )}

        <Dialog
          open={renaming !== null}
          onClose={() => setRenaming(null)}
          title="Rename this topic"
        >
          {renaming ? (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault()
                const name = renaming.name.trim()
                if (!name) return
                void run(() => site.updateTopic(renaming.id, { name })).then(
                  (ok) => ok && setRenaming(null),
                )
              }}
            >
              <Input
                label="Name"
                value={renaming.name}
                maxLength={80}
                hint="This also changes the topic’s address on your website, so a link somebody already shared will stop working."
                onChange={(e) => setRenaming({ ...renaming, name: e.target.value })}
              />
              <div className="flex gap-2">
                <Button type="submit" loading={busy} loadingText="Saving…">
                  Save
                </Button>
                <Button type="button" variant="ghost" onClick={() => setRenaming(null)}>
                  Cancel
                </Button>
              </div>
            </form>
          ) : null}
        </Dialog>

        <ConfirmDialog
          open={removing !== null}
          onClose={() => setRemoving(null)}
          title={removing ? `Delete “${removing.name}”?` : 'Delete this topic?'}
          confirmLabel="Delete the topic"
          cancelLabel="Keep it"
          tone="danger"
          busy={busy}
          busyLabel="Deleting…"
          onConfirm={async () => {
            if (!removing) return
            if (await run(() => site.deleteTopic(removing.id))) setRemoving(null)
          }}
        >
          <p className="text-muted-foreground text-sm">
            <strong className="text-foreground font-medium">No post is deleted.</strong> The posts
            filed under it stay exactly as they are — they simply stop being filed under this topic,
            and it disappears from your website’s blog.
          </p>
        </ConfirmDialog>
      </CardBody>
    </Card>
  )
}

export function SiteBlog() {
  const { can } = useEvaluation()
  const [posts, setPosts] = useState<Post[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    site
      .posts()
      .then((body) => live && setPosts(body.data))
      .catch(() => {
        if (!live) return
        setPosts([])
        setError('Your posts could not be loaded. Reload the page to try again.')
      })
    return () => {
      live = false
    }
  }, [])

  return <BlogList posts={posts} error={error} creatable={can(SITE, 'can_create')} />
}

export function BlogList({
  posts,
  error = null,
  creatable,
}: {
  /** `null` while loading. */
  posts: Post[] | null
  error?: string | null
  creatable: boolean
}) {
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<BlogFilter>('all')

  const counts = useMemo(() => {
    const live = (posts ?? []).filter((p) => p.status !== 'archived')
    return {
      all: live.length,
      published: live.filter((p) => p.status === 'published').length,
      draft: live.filter((p) => p.status === 'draft').length,
    }
  }, [posts])
  const shown = useMemo(() => filterPosts(posts ?? [], query, filter), [posts, query, filter])

  const write = creatable ? (
    <Link to="/site/blog/new" className={buttonClass()} data-write-post="">
      <PenLine size={15} aria-hidden /> Write a post
    </Link>
  ) : undefined

  return (
    <EditPage
      title="Blog"
      description={BLOG_DESCRIPTION}
      back={{ to: '/site', label: 'Your website' }}
      actions={write}
    >
      {error && <Alert tone="error">{error}</Alert>}

      {posts === null ? (
        <div className="space-y-2" aria-label="Loading posts">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-20 rounded-xl" />
          ))}
        </div>
      ) : counts.all === 0 ? (
        <EmptyState
          variant="outline"
          title="Nothing written yet"
          description="Your first post could be a note on a new rule, a filing date your clients should not miss, or news about the firm."
          action={creatable ? write : undefined}
        />
      ) : (
        <div className="space-y-4">
          {/* C24 item 15 — the firm's own topic list (ADR-0050 §1). */}
          <TopicManager editable={creatable} />
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
            <div className="relative min-w-0 flex-1">
              <Search
                size={16}
                className="text-muted-foreground pointer-events-none absolute top-1/2 left-3.5 z-10 -translate-y-1/2"
                aria-hidden
              />
              <Input
                aria-label="Search posts"
                placeholder="Search by title, summary or author"
                className="pl-10"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div
              role="radiogroup"
              aria-label="Show"
              className="border-border bg-card inline-flex shrink-0 self-start rounded-lg border p-1 sm:self-auto"
            >
              {(
                [
                  ['all', 'All'],
                  ['published', 'Published'],
                  ['draft', 'Drafts'],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  role="radio"
                  aria-checked={filter === value}
                  data-filter={value}
                  onClick={() => setFilter(value)}
                  className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium transition-colors ${
                    filter === value
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {label}
                  <span
                    className={`text-xs tabular-nums ${filter === value ? 'opacity-80' : 'text-muted-foreground'}`}
                  >
                    {counts[value]}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {shown.length === 0 ? (
            <EmptyState
              variant="outline"
              title={query.trim() ? 'No post matches that search' : `No ${filter === 'draft' ? 'drafts' : 'published posts'} yet`}
              description={
                query.trim()
                  ? 'Try another word, or clear the search to see every post.'
                  : filter === 'draft'
                    ? 'Every post you have written is published.'
                    : 'A post shows here once its status is Published.'
              }
              action={
                <button
                  type="button"
                  className={buttonClass({ variant: 'outline' })}
                  onClick={() => {
                    setQuery('')
                    setFilter('all')
                  }}
                >
                  Show every post
                </button>
              }
            />
          ) : (
            <ul className="space-y-2" aria-label="Posts">
              {shown.map((post) => (
                <PostRow key={post.id} post={post} />
              ))}
            </ul>
          )}
        </div>
      )}
    </EditPage>
  )
}

function PostRow({ post }: { post: Post }) {
  const tone = post.status === 'published' ? 'success' : 'neutral'
  return (
    <li>
      <Link
        to={`/site/blog/${post.id}`}
        data-post-row={post.id}
        className="group border-border bg-surface-raised hover:border-primary/40 flex items-center gap-3 rounded-xl border p-3 transition-shadow hover:shadow-sm sm:gap-4"
      >
        <div className="bg-muted text-muted-foreground flex h-14 w-16 shrink-0 items-center justify-center self-start overflow-hidden rounded-lg sm:w-20 sm:self-center">
          {post.cover_image_url ? (
            <img src={post.cover_image_url} alt="" loading="lazy" className="h-full w-full object-cover" />
          ) : (
            <FileText size={20} aria-hidden />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-foreground line-clamp-2 text-sm font-semibold group-hover:underline sm:truncate">
            {post.title}
          </p>
          {post.summary && <p className="text-muted-foreground hidden truncate text-sm sm:block">{post.summary}</p>}
          <p className="text-muted-foreground mt-0.5 flex min-w-0 items-center gap-2 text-xs">
            {/* On a phone the status sits under the title, so the title keeps the width. */}
            <Badge tone={tone} size="sm" className="sm:hidden">
              {STATUS_LABEL[post.status]}
            </Badge>
            <span className="truncate">{postMeta(post)}</span>
          </p>
        </div>
        <Badge tone={tone} className="hidden sm:inline-flex">
          {STATUS_LABEL[post.status]}
        </Badge>
        <ChevronRight size={16} className="text-muted-foreground hidden shrink-0 sm:block" aria-hidden />
      </Link>
    </li>
  )
}
